/*
 * Figma `126:8` — SA3-08 · Portal CMS.
 *
 * `EditorTemplate`: the front page's copy on the left, a preview of what a visitor sees on the
 * right, and the per-event visibility list beneath — because on this screen "what the public sees"
 * is two separate things that are easy to confuse.
 *
 * `salesMode` and `portalVisible` are different questions and the old panel put them side by side
 * as if they were one. `portalVisible` decides whether an event is *listed* on the front page;
 * `salesMode` decides whether its own URL answers at all. An event can be unlisted and still
 * selling to anyone with the link, which is exactly what `uat` is for — and an operator who reads
 * "hidden" as "nobody can buy" will eventually hide the wrong thing. Each row now says both, in
 * those terms.
 */
import {
  Alert, Button, Card, EditorTemplate, SaveBar, Select, StatusBadge, TextField, Textarea, Toggle,
} from '../..';
import type { SaveState } from '../..';
import type { Destination } from '../../destinations';
import type { EventSalesMode, PortalSettingsDto, TenantSummaryDto } from '../../../api';
import { usePortalCms } from './usePortalCms';

const SALES_MODE_LABEL: Record<EventSalesMode, string> = {
  public: 'ขายสาธารณะ', uat: 'ขายเฉพาะคนมีลิงก์', closed: 'ปิดการขาย',
};

export interface PortalCmsState {
  settings: PortalSettingsDto;
  setSettings: (next: PortalSettingsDto) => void;
  dirty: boolean;
  saving: boolean;
  justSaved: boolean;
  saveError: string;
  loading: boolean;
  loadError: string;
  busyEventId: string | null;
  save: () => Promise<void>;
  discard: () => void;
  setEventPortal: (eventId: string, patch: { portalVisible?: boolean; carouselVisible?: boolean }) => Promise<void>;
  setSalesMode: (eventId: string, mode: EventSalesMode) => Promise<void>;
}

export function PortalCmsScreen({ destination, state, events }: {
  destination: Destination;
  state: PortalCmsState;
  events: readonly TenantSummaryDto[];
}) {
  const {
    settings, setSettings, dirty, saving, justSaved, saveError, loading, loadError,
    busyEventId, save, discard, setEventPortal, setSalesMode,
  } = state;

  const saveState: SaveState =
    saveError ? 'error' : saving ? 'saving' : dirty ? 'dirty' : justSaved ? 'saved' : 'clean';

  return (
    <EditorTemplate
      gate={{ destination, loading, error: loadError || null }}
      sections={
        <>
          <Card title="ส่วนชวนผู้จัดงาน" sub="บล็อกบนหน้าแรกที่ชวนคนมาเปิดใช้ JamTick">
            <Toggle
              label="แสดงส่วนนี้บนหน้าแรก"
              checked={settings.organizerSectionEnabled}
              onChange={(e) => setSettings({ ...settings, organizerSectionEnabled: e.target.checked })}
              consequence="ปิดแล้วหน้าแรกจะเหลือเฉพาะรายการอีเวนต์"
            />
            <TextField
              label="หัวข้อ"
              value={settings.organizerSectionTitle}
              onChange={(e) => setSettings({ ...settings, organizerSectionTitle: e.target.value })}
            />
            <Textarea
              label="คำอธิบาย"
              limit={300}
              value={settings.organizerSectionSubtitle}
              onChange={(e) => setSettings({ ...settings, organizerSectionSubtitle: e.target.value })}
            />
          </Card>

          <Card
            title={`การ์ดจุดขาย · ${settings.organizerCards.length}`}
            action={
              <Button
                size="sm"
                onClick={() => setSettings({
                  ...settings,
                  organizerCards: [...settings.organizerCards, { emoji: '✨', title: '', desc: '' }],
                })}
              >
                ＋ เพิ่มการ์ด
              </Button>
            }
          >
            {settings.organizerCards.length === 0 && (
              <p className="jt-field-hint">ยังไม่มีการ์ด — ส่วนนี้จะแสดงเฉพาะหัวข้อกับคำอธิบาย</p>
            )}
            {settings.organizerCards.map((card, i) => (
              <div key={i} className="jt-repeater-row" role="group" aria-label={card.title || `การ์ดที่ ${i + 1}`}>
                <div className="jt-repeater-head">
                  <span className="jt-repeater-title">{card.title || `การ์ดที่ ${i + 1}`}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`ลบ ${card.title || `การ์ดที่ ${i + 1}`}`}
                    onClick={() => setSettings({
                      ...settings,
                      organizerCards: settings.organizerCards.filter((_, j) => j !== i),
                    })}
                  >
                    ลบ
                  </Button>
                </div>
                <div className="jt-field-grid">
                  <TextField
                    label="ไอคอน"
                    value={card.emoji}
                    onChange={(e) => setSettings({
                      ...settings,
                      organizerCards: settings.organizerCards.map((c, j) => (j === i ? { ...c, emoji: e.target.value } : c)),
                    })}
                  />
                  <TextField
                    label="หัวข้อการ์ด"
                    value={card.title}
                    onChange={(e) => setSettings({
                      ...settings,
                      organizerCards: settings.organizerCards.map((c, j) => (j === i ? { ...c, title: e.target.value } : c)),
                    })}
                  />
                </div>
                <TextField
                  label="คำอธิบายการ์ด"
                  value={card.desc}
                  onChange={(e) => setSettings({
                    ...settings,
                    organizerCards: settings.organizerCards.map((c, j) => (j === i ? { ...c, desc: e.target.value } : c)),
                  })}
                />
              </div>
            ))}
          </Card>

          <Card
            title="อีเวนต์บนหน้าแรก"
            sub="สองคำถามคนละเรื่อง — แสดงในรายการหรือไม่ กับ URL ของงานเปิดให้ซื้อหรือไม่"
          >
            <Alert tone="info" title="ซ่อนจากหน้าแรก ไม่เท่ากับปิดการขาย">
              อีเวนต์ที่ไม่แสดงบนหน้าแรกยังขายได้ตามปกติสำหรับคนที่มีลิงก์ — ถ้าต้องการหยุดขายจริง ให้เปลี่ยน
              “โหมดการขาย” เป็นปิดการขาย
            </Alert>
            {events.map((event) => (
              <div key={event.id} className="jt-repeater-row" role="group" aria-label={event.name || event.slug}>
                <div className="jt-repeater-head">
                  <span className="jt-repeater-title">{event.name || event.slug}</span>
                  <StatusBadge tone={event.salesMode === 'public' ? 'success' : event.salesMode === 'uat' ? 'warning' : 'neutral'}>
                    {SALES_MODE_LABEL[event.salesMode ?? 'public']}
                  </StatusBadge>
                </div>
                <Toggle
                  label="แสดงในรายการบนหน้าแรก"
                  checked={!!event.portalVisible}
                  disabled={busyEventId === event.id}
                  onChange={(e) => void setEventPortal(event.id, { portalVisible: e.target.checked })}
                  consequence="ปิดแล้วยังซื้อได้ถ้ามีลิงก์"
                />
                <Toggle
                  label="แสดงในแถบสไลด์ด้านบน"
                  checked={!!event.carouselVisible}
                  disabled={busyEventId === event.id}
                  onChange={(e) => void setEventPortal(event.id, { carouselVisible: e.target.checked })}
                />
                <Select
                  label="โหมดการขาย"
                  value={event.salesMode ?? 'public'}
                  disabled={busyEventId === event.id}
                  options={(['public', 'uat', 'closed'] as EventSalesMode[]).map((m) => ({ value: m, label: SALES_MODE_LABEL[m] }))}
                  onChange={(e) => void setSalesMode(event.id, e.target.value as EventSalesMode)}
                  hint="นี่คือสวิตช์ที่ตัดสินว่า URL ของงานนี้ตอบหรือไม่"
                />
              </div>
            ))}
          </Card>
        </>
      }
      preview={
        <Card title="ตัวอย่างหน้าแรก" sub="ส่วนชวนผู้จัดงานที่ผู้เข้าชมจะเห็น">
          {settings.organizerSectionEnabled ? (
            <div className="jt-lp-preview">
              <div className="jt-lp-hero">
                <p className="jt-lp-title">{settings.organizerSectionTitle || 'ยังไม่ได้กรอกหัวข้อ'}</p>
                <p className="jt-lp-note">{settings.organizerSectionSubtitle}</p>
              </div>
              <div className="jt-lp-body">
                {settings.organizerCards.map((card, i) => (
                  <p key={i} className="jt-lp-line">{card.emoji} {card.title || '—'} · {card.desc}</p>
                ))}
              </div>
            </div>
          ) : (
            <p className="jt-field-hint">ส่วนนี้ถูกปิดอยู่ — หน้าแรกจะแสดงเฉพาะรายการอีเวนต์</p>
          )}
        </Card>
      }
      stickyActions={
        <SaveBar
          state={saveState}
          error={saveError || null}
          onSave={() => void save()}
          onDiscard={dirty ? discard : undefined}
          consequence="บันทึกแล้วหน้าแรกสาธารณะจะเปลี่ยนทันที · สวิตช์ของแต่ละอีเวนต์มีผลทันทีโดยไม่ต้องบันทึก"
        />
      }
    />
  );
}

export function PortalCmsDestination({ token, destination, events, onRefresh }: {
  token: string;
  destination: Destination;
  events: readonly TenantSummaryDto[];
  onRefresh?: () => void;
}) {
  const state = usePortalCms(token, onRefresh);
  return <PortalCmsScreen destination={destination} state={state} events={events} />;
}
